import { Countries } from '@platform/client';
import { ProfessionalApplicationParameters } from '@platform/client/schemas';
import { FormsInputDropdown, InlineFormInput, TimezonePicker } from '@platform/components';
import { SubmitHandler, useForm } from 'react-hook-form';
import { ApplicationPageProps } from '../../Application';
import { useApplicationLocalStorage } from '../../application-hook';
import { ApplicationBackground } from '../../ApplicationBackground';
import { ApplicationPageContent } from '../../components/ApplicationPageContent';
import BackgroundImage from '../../images/Screen4.svg';

type FormValues = {
  country: string;
  city: string;
  timezone: string;
};

export const LocationSelection = (props: ApplicationPageProps) => {
  const { onBack, onContinue, progress, isEditProfile, onSaveExit, dataKey } = { ...props };

  const { updateApplicationData, getApplicationDataByKey } =
    useApplicationLocalStorage<ProfessionalApplicationParameters>(dataKey);

  const countriesList = Countries.useGetCountries();

  const countryOptions =
    countriesList.isSuccess ?
      countriesList.data.map((country) => ({
        text: country.name,
        textValue: country.id.toString(),
      }))
    : [];

  const {
    register,
    handleSubmit,
    control,
    watch,
    formState: { errors },
    setValue,
  } = useForm<FormValues>({
    mode: 'onBlur',
    reValidateMode: 'onChange',
    defaultValues: {
      country: getApplicationDataByKey('country')?.toString() || '',
      city: getApplicationDataByKey('city') || '',
      timezone:
        getApplicationDataByKey('timezone') || Intl.DateTimeFormat().resolvedOptions().timeZone,
    },
  });

  const submit: SubmitHandler<FormValues> = (data) => {
    updateApplicationData({
      country: Number(data.country),
      city: data.city,
      timezone: data.timezone,
    });
    onContinue();
  };

  const handleBack = () => {
    updateApplicationData({
      country: Number(watch('country')),
      city: watch('city'),
      timezone: watch('timezone'),
    });
    onBack();
  };

  const handleTimezoneChange = (timezone: string) => {
    setValue('timezone', timezone);
  };

  return (
    <ApplicationBackground
      progress={progress}
      onBack={handleBack}
      onSubmit={handleSubmit(submit)}
      isEditProfile={isEditProfile}
      onSaveAndExit={onSaveExit}
      backgroundImage={BackgroundImage}
    >
      <ApplicationPageContent
        title='Where are you currently based?'
        description='Let organizations know where you are, and help us match you with projects in a compatible timezone.'
      >
        <FormsInputDropdown
          control={control}
          name='country'
          rules={{ required: 'Select your country to continue' }}
          label='Country'
          placeholder='Start typing country name'
          items={countryOptions}
          type='default'
          error={errors.country}
        />
        <InlineFormInput
          label='City'
          placeholder='Enter your city'
          register={() =>
            register('city', {
              required: 'City is required',
            })
          }
          inputType='text'
          errors={errors.city}
        />
        <TimezonePicker timezone={watch('timezone')} updateTimezone={handleTimezoneChange} />
      </ApplicationPageContent>
    </ApplicationBackground>
  );
};
